"use client";

// Shared UI primitives for the design system. Soft, ambient, minimal.
// The hero controls — PillButton (label + circular icon badge) and Field
// (lit-underline input) — are defined here so the new language trickles into
// every surface that imports them.

import React, { useId } from "react";
import { motion, useSpringHover } from "../lib/motion";

/* ------------------------------------------------------------------ *
 * Button — the small utility control (kept for dashboards/settings).  *
 * For onboarding/CTA work prefer <PillButton>.                        *
 *                                                                     *
 * Motion: a soft spring lift on hover + gentle press on tap (snappy   *
 * preset, no bounce). It's purely additive — the existing CSS only    *
 * transitions background/border/opacity, so the spring drives the     *
 * orthogonal `transform` axis and the two never fight. Reduced motion *
 * drops the lift/press entirely (useSpringHover handles that).        *
 * ------------------------------------------------------------------ */

type ButtonProps = {
  variant?: "primary" | "ghost" | "soft";
  size?: "sm" | "md";
  as?: "button" | "a";
  href?: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "style" | "className">;

const PAD = { sm: "0.5rem 0.9rem", md: "0.72rem 1.4rem" };

export function Button({
  variant = "primary",
  size = "md",
  as = "button",
  href,
  children,
  className,
  style,
  ...rest
}: ButtonProps) {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: PAD[size],
    borderRadius: "var(--radius-pill)",
    fontFamily: "inherit",
    fontSize: size === "sm" ? "0.8rem" : "0.92rem",
    letterSpacing: "0.01em",
    cursor: "pointer",
    textDecoration: "none",
    transition: "background var(--dur) var(--ease-soft), border-color var(--dur) var(--ease-soft), opacity var(--dur) ease",
    whiteSpace: "nowrap",
  };
  const variants: Record<string, React.CSSProperties> = {
    primary: { background: "var(--accent)", color: "var(--on-accent)", border: "1px solid transparent" },
    soft: { background: "var(--surface-2)", color: "var(--ink)", border: "1px solid var(--hair)" },
    ghost: { background: "transparent", color: "var(--ink-2)", border: "1px solid var(--line)" },
  };
  const merged = { ...base, ...variants[variant], ...style };
  const hover = useSpringHover();
  if (as === "a") {
    return (
      <motion.a
        href={href}
        className={className}
        style={merged}
        {...hover}
        {...(rest as React.ComponentPropsWithoutRef<typeof motion.a>)}
      >
        {children}
      </motion.a>
    );
  }
  return (
    <motion.button
      className={className}
      style={merged}
      {...hover}
      {...(rest as React.ComponentPropsWithoutRef<typeof motion.button>)}
    >
      {children}
    </motion.button>
  );
}

/* ------------------------------------------------------------------ *
 * Chevron — THE forward mark, everywhere (law_chevrons). A single      *
 * chevron with a 3px stroke, rounded caps + joins, no stem. Stem       *
 * arrows (→ with a shaft) are banned from every button and control.    *
 * `dir` turns it; default points right.                                *
 * ------------------------------------------------------------------ */

export function Chevron({
  size = 20,
  className,
  dir = "right",
}: {
  size?: number;
  className?: string;
  dir?: "right" | "left" | "up" | "down";
}) {
  const rot = dir === "left" ? 180 : dir === "up" ? -90 : dir === "down" ? 90 : 0;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
      style={rot ? { transform: `rotate(${rot}deg)` } : undefined}
    >
      {/* optically centered: the glyph spans x 8.75→15.25 around the 12 midline */}
      <path d="M8.75 5.5 15.25 12l-6.5 6.5" />
    </svg>
  );
}

/** Back-compat alias — every existing ChevronRight callsite now renders the
 *  rounded 3px law chevron. */
export function ChevronRight({ size = 20, className }: { size?: number; className?: string }) {
  return <Chevron size={size} className={className} dir="right" />;
}

/* ------------------------------------------------------------------ *
 * PillButton — the hero control. Label left, circular icon badge      *
 * right. variant="oauth" (quiet, white badge) | "primary" (lime CTA   *
 * with a living gradient arrow badge). Handles loading + arrow lean.  *
 * ------------------------------------------------------------------ */

type PillButtonProps = {
  variant?: "primary" | "oauth";
  /** Badge content. Defaults to the forward arrow on the primary variant. */
  icon?: React.ReactNode;
  loading?: boolean;
  /** Stagger the badge's settle-in (seconds). */
  iconDelay?: number;
  as?: "button" | "a";
  href?: string;
  /** as="a" only — a file the link hands over (export my data) */
  download?: boolean | string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "style" | "className">;

export function PillButton({
  variant = "primary",
  icon,
  loading = false,
  iconDelay,
  as = "button",
  href,
  children,
  className,
  style,
  ...rest
}: PillButtonProps) {
  const cls = ["btn-pill", variant === "primary" ? "btn-pill--primary" : "", className]
    .filter(Boolean)
    .join(" ");
  const badge = icon ?? (variant === "primary" ? <Chevron size={18} className="btn-pill-arrow" /> : null);

  const inner = (
    <>
      <span className="btn-pill-label">{children}</span>
      <span className="btn-pill-icon" aria-hidden="true">
        {loading ? (
          <span className="btn-spinner" />
        ) : (
          <span className="icon-in" style={iconDelay ? { animationDelay: `${iconDelay}s` } : undefined}>
            {badge}
          </span>
        )}
      </span>
    </>
  );

  if (as === "a") {
    return (
      <a href={href} className={cls} style={style} aria-busy={loading || undefined} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {inner}
      </a>
    );
  }
  return (
    // ⚠️ `disabled` comes AFTER the spread, always. It used to sit before it, so a
    // caller's `disabled={false}` (OAuthButton passes `blocked`, which is usually
    // false) overwrote `loading || …` and a loading pill could be pressed again.
    <button className={cls} style={style} aria-busy={loading || undefined} {...rest} disabled={loading || (rest as React.ButtonHTMLAttributes<HTMLButtonElement>).disabled}>
      {inner}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Field — lowercase label + lit-underline input + optional error rule.*
 * The onboarding input atom. Forwards every native <input> prop.      *
 * ------------------------------------------------------------------ */

type FieldProps = {
  label: string;
  error?: string | null;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Field = React.forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, id, className, ...input },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="field" data-error={error ? "true" : undefined}>
      <label className="field-label" htmlFor={fieldId}>
        {label}
      </label>
      <input
        ref={ref}
        id={fieldId}
        className={["input-soft", className].filter(Boolean).join(" ")}
        aria-invalid={error ? true : undefined}
        {...input}
      />
      {error ? <span className="field-error">{error}</span> : null}
    </div>
  );
});
